import {
  deleteRequest,
  getRequest,
  putRequest,
  postRequest,
} from "@pazion/pazion-core/src/services/auth-service";
import { stringifyUrl } from "@pazion/pazion-core/src/services/base-service";
import { IApiProduct } from "../models/product";
import { IWarehouse } from "../models/warehouse";
import { IApiCategory } from "../models/category";
import { ICustomOrderLine } from "../models/order";
import { IdeliveryPostData } from "../models/delivery.model";
import { authentication } from "@pazion/pazion-core/src/store";

const basePath = "catalog";

interface CustomOrderLine {
  type: string;
  data: { [key: string]: any }[];
}

/**
Products API methods
 */
export async function getViewProducts(
  filter?: any,
  limit?: number,
  pagination?: boolean
) {
  let prodFilter = filter || {};
  if (filter || pagination) {
    const url = stringifyUrl({
      url: "catalog/view-products",
      query: {
        filter: JSON.stringify(prodFilter),
        limit: limit,
      },
    });
    return (await getRequest(url)).data;
  }
  return (await getRequest("catalog/view-products")).data;
}

export async function getProductType(filter?: any) {
  let prodFilter = filter || {};
  if (filter) {
    const url = stringifyUrl({
      url: "catalog/product-types",
      query: {
        filter: JSON.stringify(prodFilter),
      },
    });
    return (await getRequest(url)).data;
  }
  return (await getRequest("catalog/product-types")).data;
}

export async function getViewProductsPaginated(queryParams?: any) {
  if (queryParams) {
    const url = stringifyUrl({
      url: "catalog/view-products",
      query: {
        ...queryParams,
      },
    });
    return (await getRequest(url)).data;
  }
  return (await getRequest("catalog/view-products")).data;
}

export async function getProductById(productId: number): Promise<IApiProduct> {
  return (await getRequest("catalog/view-products/" + productId)).data;
}

export async function getAllProducts() {
  return (await getRequest("catalog/products")).data;
}
export async function getProduct(id: number, queryParams?: any) {
  const url = stringifyUrl({
    url: `catalog/products/${id}`,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(url)).data;
}
export async function createProduct(data: object) {
  return (await postRequest("catalog/products", data)).data;
}
export async function updateProduct(data: object, id: number) {
  return (await putRequest(`catalog/products/${id}`, data)).data;
}
export async function deleteProduct(id: number) {
  return (await deleteRequest(`catalog/products/${id}`)).data;
}

/**
 * Import products
 * @returns status
 */
export default async function bulkProductsImport(file: File) {
  let formData = new FormData();
  formData.append("file", file);
  return (
    await postRequest(`${basePath}/products/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  ).data;
}

export async function combineFilesAndDownload(files: number[]){
  return (await postRequest(`file-combiner`,{files})).data;
}


//NOTE: Products attributes
export async function getProductAttributes() {
  return (
    await getRequest(
      `${basePath}/product-attributes?expand=productAttributeType`
    )
  ).data;
}

export async function getProductAttributeValues(filter?: object) {
  const url = stringifyUrl({
    url: `${basePath}/product-attribute-values`,
    query: {
      expand: 'productAttribute,productAttributeType',
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url)).data;
}
export async function setProductAttrValues(data: any) {
  return (await postRequest(`${basePath}/product-attribute-values`, data)).data;
}

export async function updateProductAttrValue(id: number, data: object) {
  return (await putRequest(`${basePath}/product-attribute-values/${id}`, data))
    .data;
}

export async function getProductCategories(): Promise<CustomOrderLine> {
  return (await getRequest(`product-categories`)).data;
}

export async function getProductMaterials(
  category: string
): Promise<CustomOrderLine> {
  return (await getRequest(`product-materials?category=${category}`)).data;
}

export async function get3dPrintMaterials(): Promise<CustomOrderLine> {
  return (await getRequest("3dprint")).data;
}

export async function getSeparateProduct(): Promise<CustomOrderLine> {
  return (await getRequest("separate-product")).data;
}

export async function getProductOrientations(): Promise<CustomOrderLine> {
  return (await getRequest(`product-orientations`)).data;
}

export async function getProductExtras(): Promise<CustomOrderLine> {
  return (await getRequest(`product-extras`)).data;
}
/**/

export async function getStockTransactionTypes() {
  return (await getRequest("catalog/stock-transaction-types")).data;
}

export async function getStock(filter?: any, limit?: number) {
  filter = filter || {};
  if (filter) {
    const url = stringifyUrl({
      url: "stock-mutations",
      query: {
        filter: JSON.stringify(filter),
        limit: limit,
      },
    });
    return (await getRequest(url)).data;
  }
  return (await getRequest("stock-mutations")).data;
}

export async function getStockDownload(queryParams?: object) {
  const baseUrl = "catalog/current-stock/download";
  const paramUrl = stringifyUrl({
    url: baseUrl,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(paramUrl, { responseType: "blob" })).data;
}

export async function createStockMutation(data: object) {
  return (await postRequest("mutate-stock", data)).data;
}

export async function getCategoriesProduct(): Promise<Array<IApiCategory>> {
  const url = stringifyUrl({
    url: "catalog/categories-products",
  });
  return (await getRequest(url)).data;
}

export async function getOrderLines(filter?: object, orderBy?:string) {
  filter = filter || {};
  orderBy = orderBy || {};
  const url = stringifyUrl({
    url: "catalog/order-lines",
    query: {
      expand: "product,attributeValue",
      expandStock:true,
      excludeSystemProduct:true,
      filter: JSON.stringify(filter),
      orderBy: JSON.stringify(orderBy),
    },
  });
  return (
    await getRequest(
      url
        ? url
        : "catalog/order-lines?expandStock=true&excludeSystemProducts=true"
    )
  ).data;
}

export async function getCustomOrderLines(
  filter?: object
): Promise<ICustomOrderLine[]> {
  const url = stringifyUrl({
    url: "app/order-lines",
    query: {
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url ? url : "app/order-lines")).data;
}

export async function addCustomOrderLine(
  params: ICustomOrderLine
): Promise<ICustomOrderLine[]> {
  return (await postRequest("app/order-lines", params)).data;
}

export async function deleteCustomOrderList(id: number) {
  const url = stringifyUrl({
    url: "app/order-lines/" + id,
  });
  return (await deleteRequest(url)).data;
}

export async function getProductStocks(queryParams?: object) {
  const url = stringifyUrl({
    url: "catalog/product-stocks",
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(url)).data;
}

/**
 * SECTION: Filfilments
 */
export async function getFulfilment(filter?: object) {
  filter = filter || {};
  const url = stringifyUrl({
    url: `${basePath}/fulfilments`,
    query: {
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url ? url : "catalog/fulfilments")).data;
}

export async function getFulfilmentTable(queryParams?: object) {
  const fullUrl = stringifyUrl({
    url: `${basePath}/fulfilments`,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(fullUrl)).data;
}

export async function getFulfilmentLineTable(queryParams?: object) {
  const fullUrl = stringifyUrl({
    url: `${basePath}/fulfilment-lines`,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(fullUrl)).data;
}



export async function getFulfilmentOrderLines(
  filter?: object,
  expand?: object
) {
  filter = filter || {};
  const url = stringifyUrl({
    url: "catalog/fulfilment-lines",
    query: {
      ...expand,
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url ? url : "catalog/fulfilment-lines")).data;
}
export async function getFulfilmentTransports(orderId?: number | null | undefined) {
  return (await getRequest("fulfilment-transports?orderId=" + orderId)).data;
}

export async function getFulfilmentStatuses() {
  return (await getRequest("fulfilment-statuses")).data;
}

export async function getPackageSizes() {
  return (await getRequest("fulfilment-package-sizes")).data;
}

/**
 * !SECTION Fulfilment attributes
 */
export async function getFulfilmentAttributes() {
  return (await getRequest(`${basePath}/fulfilment-attributes`)).data;
}

export async function getFulfulmentAttrValue(filter?: object) {
  const url = stringifyUrl({
    url: `${basePath}/fulfilment-attribute-values`,
    query: {
      expand: "fulfilmentAttribute",
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url)).data;
}

export async function setFulfilmentAttrValues(data: any) {
  return (await postRequest(`${basePath}/fulfilment-attribute-values`, data))
    .data;
}

/** **/

export async function updateFullfilment(id: number, data: any) {
  return (await putRequest(`${basePath}/fulfilments/${id}`, data)).data;
}

export async function createOrder(reference: string) {
  return (await postRequest("catalog/orders/0", { reference })).data;
}

export async function getOrdersOnly(filter?: object) {
  filter = filter || {};
  const url = stringifyUrl({
    url: "catalog/orders",
    query: {
      expand: "orderLine",
      ...filter,
    },
  });

  return (await getRequest(url)).data;
}

export async function getOrderlinesIncludingOrderFields(filter?: object) {
  filter = filter || {};
  const url = stringifyUrl({
    url: "catalog/order-lines-combined",
    query: {
      ...filter,
    },
  });
  return (await getRequest(url)).data;
}

export async function createOrderWithData(data: object) {
  return (await postRequest("catalog/orders/0", data)).data;
}

export async function updateOrder(orderId: number, data: object) {
  if (orderId > 0) {
    return (await putRequest("catalog/orders/" + orderId, data)).data;
  }
  return (await postRequest("catalog/orders/" + orderId, data)).data;
}

export async function getOrderDetails(orderId: number, hasProduction: boolean, expand?: string) {
  const url = stringifyUrl({
    url: "catalog/orders/" + orderId,
    query: {
      productionManagement: hasProduction,
      expand: expand,
    },
  });
  return (await getRequest(url)).data;
}

export async function getOrderAttributes() {
  return (await getRequest("catalog/order-attributes")).data;
}
export async function getOrderLineAttributes() {
  return (await getRequest("catalog/order-line-attributes")).data;
}

export async function getOrderAttrValue(filter?: object) {
  const url = stringifyUrl({
    url: "catalog/order-attribute-values",
    query: {
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url)).data;
}

export async function getOrderLineAttrValues(filter?: object) {
  const url = stringifyUrl({
    url: "catalog/order-line-attribute-values",
    query: {
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url)).data;
}

// NOTE: Create: add attribute to order
export async function setOrdeAttrValues(data: any) {
  return (await postRequest(`catalog/order-attribute-values`, data)).data;
}
// NOTE: Create: add attribute to orderLine
export async function setOrderLineAttrValues(data: any) {
  return (await postRequest(`catalog/order-line-attribute-values`, data)).data;
}
// NOTE: UPDATE: orderLine or order, product attributes
export async function setAttributeValue(
  type: string,
  id: number,
  data: object
) {
  let typeUrl;
  switch (type) {
    case "order":
      typeUrl = "order-attribute-values/";
      break;
    case "orderLine":
      typeUrl = "order-line-attribute-values/";
      break;
    case "product":
      typeUrl = "product-attribute-values/";
      break;
    case "offer":
      typeUrl = "offer-attribute-values/";
      break;
    case "account":
      typeUrl = "account-attribute-values/";
      break;
    case "fulfilment":
      typeUrl = "fulfilment-attribute-values/";
      break;
    case "invoice":
      typeUrl = "invoice-attribute-values/";
      break;
    default:
      return {};
  }
  return (await putRequest("catalog/" + typeUrl + id, data)).data;
}

/**
 * NOTE: Delete Attributes
 * @param instanceId
 * @param instanceType
 * @returns
 */
export async function deleteAttributesValue(
  instanceId: number,
  instanceType: string
) {
  let typeUrl;
  switch (instanceType) {
    case "order":
      typeUrl = "order-attribute-values/";
      break;
    case "orderLine":
      typeUrl = "order-line-attribute-values/";
      break;
    case "product":
      typeUrl = "product-attribute-values/";
      break;
    case "offer":
      typeUrl = "offer-attribute-values/";
      break;
    case "fulfilment":
      typeUrl = "fulfilment-attribute-values/";
      break;
    case "account":
      typeUrl = "account-attribute-values/";
      break;
    case "rma":
      typeUrl = "rma-attribute-values/";
      break;
    default:
      return {};
  }

  const fullUrl = stringifyUrl({
    url: "catalog/" + typeUrl + instanceId,
    query: {
      hard: true,
    },
  });

  return (await deleteRequest(fullUrl)).data;
}

export async function getMarketplaces(query?: object) {
  const fullUrl = stringifyUrl({
    url: "catalog/marketplaces",
    query,
  });
  return (await getRequest(fullUrl)).data;
}

export async function deleteOrderById(orderId: number) {
  return (await deleteRequest("catalog/orders/" + orderId)).data;
}

export async function deleteOrderLine(orderLineId: number) {
  return (await deleteRequest("catalog/order-lines/" + orderLineId)).data;
}

export async function deleteFulfilment(fulfilmentId: number) {
  return (await deleteRequest("catalog/fulfilments/" + fulfilmentId)).data;
}

export async function deleteReturn(returnId: number) {
  return (await deleteRequest("catalog/rma/" + returnId)).data;
}

export async function updateOrderLine(orderLineId: number, data?: any) {
  if (orderLineId > 0) {
    return (await putRequest("catalog/order-lines/" + orderLineId, data)).data;
  }
  return (await postRequest("catalog/order-lines", data)).data;
}

export async function getCatalogDashboardDetails() {
  return (await getRequest("catalog-dashboard")).data;
}

export async function getWarehouses(query?: object): Promise<IWarehouse[]> {
  const fullUrl = stringifyUrl({
    url: "catalog/warehouses",
    query,
  });
  return (await getRequest(fullUrl)).data;
}

export async function createBatchShipping({
  orderIds,
}: {
  orderIds: { order: number }[];
}) {
  const url = `${authentication.userConfig.base.url}/api/v3`;
  return await postRequest(
    `${url}/order-batchfulfilment`,
    JSON.stringify(orderIds)
  ).data;
}

export async function createMultipleOrderDelivery({
  orderLines,
}: {
  orderLines: { id: number; quantity: number | string }[];
}) {
  const url = `${authentication.userConfig.base.url}/api/v3`;
  return (
    await postRequest(`${url}/order-delivery`, {
      orderLine: [...orderLines],
    })
  ).data;
}

// NOTE: Accounts API
export async function getAccounts(filter = {}) {
  filter = filter || {};
  let url = stringifyUrl({
    url: "catalog/accounts",
    query: {
      filter: JSON.stringify(filter),
    },
  });

  return (await getRequest(url)).data;
}

export async function getAccountById(accountId?: number) {
  return (await getRequest("catalog/accounts/" + accountId)).data;
}

export async function getAccountUsers(filter: any) {
  let url = stringifyUrl({
    url: `${`${authentication.userConfig.base.url}/api/v3`}/catalog/account-users`,
    query: {
      filter: JSON.stringify(filter),
    },
  });

  return (await getRequest(url)).data;
}

export async function setAccountUser({
  accountId = 1,
  userId = 9,
}: {
  accountId: number;
  userId: number;
}) {
  return (
    await postRequest(`catalog/account-users/${accountId}`, {
      accountId,
      userId,
    })
  ).data;
}

export async function deleteAccountUser(id: number) {
  return (await deleteRequest(`catalog/account-users/${id}`)).data;
}

export async function deleteAccount(accountId?: number, hard?: boolean) {
  const url = stringifyUrl({
    url: "catalog/accounts/" + accountId,
    query: {
      hard: hard,
    },
  });
  return (await deleteRequest(url)).data;
}

export async function updateAccount(accountId: number, data: any) {
  data.id = accountId;
  if (accountId == 0) {
    return (await postRequest("catalog/accounts/0", data)).data;
  }
  return (await putRequest("catalog/accounts/" + accountId, data)).data;
}

export async function getAdressById(addressId?: number) {
  return (await getRequest("catalog/addresses/" + addressId)).data;
}

export async function updateAdress(
  addressId: number,
  accountId: number,
  data: any
) {
  data.accountId = accountId;
  data.id = addressId;
  if (addressId == 0) {
    return (await postRequest("catalog/addresses/0", data)).data;
  }
  return (await putRequest("catalog/addresses/" + addressId, data)).data;
}

export async function deleteAdress(addressId: number) {
  return (await deleteRequest("catalog/addresses/" + addressId)).data;
}

export async function getAdressForAccount(
  accountId: number,
  type?: number | string
) {
  const url = stringifyUrl({
    url: "catalog/addresses",
    query: {
      filter: JSON.stringify({ account: accountId, addressType: type }),
    },
  });
  return (await getRequest(url)).data;
}
export async function getAddressesTypes() {
  return (await getRequest(`${basePath}/addresses-types`)).data;
}

export async function getBolSettings() {
  return (await getRequest("settings-bol")).data;
}

//SECTION: Delivery
export async function postOrderDelivery(item: IdeliveryPostData) {
  return (await postRequest("order-delivery", item)).data;
}

export async function updateDeliveryData(item: IdeliveryPostData) {
  return (await putRequest("order-delivery", item)).data;
}

export async function getInvoices(queryParams?: object) {
  const fullUrl = stringifyUrl({
    url: `${basePath}/invoices`,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(fullUrl)).data;
}

export async function getInvoice(id: number, queryParams?: object) {
  const fullUrl = stringifyUrl({
    url: `${basePath}/invoices/${id}`,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(fullUrl)).data;
}

export async function deleteInvoice(id: number) {
  const fullUrl = stringifyUrl({
    url: `${basePath}/invoices/${id}`,
  });
  return (await deleteRequest(fullUrl)).data;
}

export async function getInvoicementAttrValue(filter?: object) {
  const url = stringifyUrl({
    url: `${basePath}/invoice-attribute-values`,
    query: {
      expand: "invoiceAttribute",
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url)).data;
}

export async function getAccountAttrValue(filter?: object) {
  const url = stringifyUrl({
    url: `${basePath}/account-attribute-values`,
    query: {
      expand: "accountAttribute",
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url)).data;
}

export async function updateInvoice(data: any) {
  return (await putRequest(`${basePath}/invoices/${data.id}`, data)).data;
}

export async function getInvoiceStatuses() {
  return (await getRequest(`invoice-statuses`)).data;
}

export async function getInvoiceAttributes() {
  return (await getRequest(`${basePath}/invoice-attributes`)).data;
}

export async function getAccountAttributes() {
  return (await getRequest(`${basePath}/account-attributes`)).data;
}

export async function setInvoiceAttrValues(data: any) {
  return (await postRequest(`${basePath}/invoice-attribute-values`, data)).data;
}
export async function setAccountAttrValues(data: any) {
  return (await postRequest(`${basePath}/account-attribute-values`, data)).data;
}

//getInvoiceAttributes

export async function getOrderStatuses(params?: { activeStatuses: number }) {
  return (await getRequest("orders-statuses", { params })).data;
}

/**
 * NOTE: Production lines API
 * */
export async function getProductionLines(queryParams: object) {
  const fullUrl = stringifyUrl({
    url: `${basePath}/production-lines`,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(fullUrl)).data;
}

export async function getProductionLine(id: number, queryParams: object) {
  const fullUrl = stringifyUrl({
    url: `${basePath}/production-lines/${id}`,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(fullUrl)).data;
}

/**
 * NOTE: product-account-price
 */
export async function getProductAccountPrice(queryParams: object) {
  const fullUrl = stringifyUrl({
    url: `${basePath}/view-account-product-prices`,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(fullUrl)).data;
}

/**
 * NOTE: Returns managment
 **/

export async function getReturns(queryParams?: object) {
  const fullUrl = stringifyUrl({
    url: `${basePath}/rma`,
    query: {
      ...queryParams,
    },
  });
  return (await getRequest(fullUrl)).data;
}

export async function getReturnsAttributes() {
  return (await getRequest(`${basePath}/rma-attributes`)).data;
}

export async function setReturnsAttrValues(data: any) {
  return (await postRequest(`${basePath}/rma-attribute-values`, data)).data;
}

export async function getReturnsAttrValues(filter?: object) {
  const url = stringifyUrl({
    url: `${basePath}/rma-attribute-values`,
    query: {
      expand: "rmaAttribute",
      filter: JSON.stringify(filter),
    },
  });
  return (await getRequest(url)).data;
}

export async function updateReturns(params?: any) {
  return (await putRequest(`${basePath}/rma/${params.id}`, params)).data;
}

export async function getLedgerCodeOptions(): Promise<any[]> {
  return (await getRequest(`catalog/ledger-codes`)).data;
}
export async function getCostCenterCodeOptions(): Promise<any[]> {
  return (await getRequest(`catalog/costcenters`)).data;
}
